import React from 'react';
import i18n from 'i18next';
import { Widget } from '@sprinklr/stories/widget/Widget';
import { Theme } from 'models/Theme/Theme';

// types
import { CollapseProps, TunablesCollapse } from 'components/Sidebar/RenderTunables/RenderTunables';

// components
import { Collapse, Flexbox, getPx } from 'components/spaceKit';
import ActionCheckboxGroup from 'components/_UI/Forms/Actions/ActionCheckboxGroup';
import ActionSelectGroup from 'components/_UI/Forms/Actions/ActionSelectGroup';

// helpers
import styled from 'components/_UI/designSystem';
import { headerTunables, opacityTunables } from 'components/Sidebar/RenderTunables/SharedTunables';
import {
    xAxisTunables,
    yAxisTunables,
    gridTunables,
    legendTunables,
    metaInfoTunables,
} from 'components/Sidebar/RenderTunables/HyperspaceSharedTunables';
import ActionCheckboxSliderGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderGroup';

const StyledCollapse = styled(Collapse)`
    [data-id='collapse-content'] > ${Flexbox} {
        > * + * {
            margin-top: ${getPx(3)};
        }
    }
`;

const CHART_TYPE_OPTIONS = [
    {
        label: i18n.t('Line'),
        value: 'line',
    },
    {
        label: i18n.t('Column'),
        value: 'column',
    },
    {
        label: i18n.t('Area'),
        value: 'area',
    },
];

function chartOptions(widget) {
    return widget.analyticsRequests[0].projections.map((metric, index) => {
        const metricName = metric.details?.alternateHeading ?? metric.heading;
        return (
            <StyledCollapse label={metricName + ''} key={`layer-${metricName + ''}-${index}`}>
                <ActionSelectGroup
                    label='Chart Type'
                    option={widget.options.baseXYChartOptions.yAxes[index]}
                    value='type'
                    options={CHART_TYPE_OPTIONS}
                />
            </StyledCollapse>
        );
    });
}

export const HyperSpaceDualAxisChartWidgetTunables = (
    widget: Widget,
    mergedTheme: Theme
): TunablesCollapse[] => {
    return [
        {
            props: {
                label: i18n.t('Dual Axis Chart'),
                startOpen: true,
            } as CollapseProps,
            children: [
                ...headerTunables(widget),
                opacityTunables(widget),
                <ActionCheckboxGroup
                    key='widget.options.zeroFill'
                    label={i18n.t('Zero Fill')}
                    option={widget.options}
                    value='zeroFill'
                />,
                gridTunables(widget),
                <ActionCheckboxSliderGroup
                    key='data-labels'
                    label={i18n.t('Data Labels')}
                    slider={{
                        value: 'fontSize',
                        min: 0,
                        max: 100,
                        option: widget.options.baseXYChartOptions?.dataLabels,
                    }}
                    checkbox={{
                        checked: true,
                        option: widget.options.baseXYChartOptions?.dataLabels,
                        value: 'enabled',
                    }}
                    showValue
                />,
            ],
        },
        {
            props: {
                label: 'Charts',
                startOpen: true,
            } as CollapseProps,
            children: [chartOptions(widget)],
        },
        metaInfoTunables({ widget, color: true, image: false }),
        xAxisTunables({ widget, mergedTheme }),
        yAxisTunables({ widget, mergedTheme, axisIsDim: false }),
        legendTunables(widget, { metric: false }),
    ];
};
